/*
 * Grade de montagem de kit com bordado por peça — ADR-0012.
 *
 * Cada peça escolhida vira uma linha própria do carrinho, com as line item
 * properties do bordado. Isso é o que o app de bundle não entrega: ele monta o
 * kit como um item só, sem lugar para a personalização de cada peça.
 *
 * Duas fontes de verdade, nenhuma delas duplicada aqui:
 *   - catálogo, faixas e variantes de cobrança vêm da section, em JSON;
 *   - quais peças aceitam bordado, com que campos, cores e prazo, vem do
 *     Live Product Options em `window.Cloudlift`. Se o app não carregar, a
 *     grade funciona sem oferecer bordado — nunca o contrário.
 */

if (!customElements.get('kit-builder')) {
  /** Formata centavos no padrão de moeda da loja. */
  const emDinheiro = (centavos) =>
    FoxTheme.Currency.formatMoney(centavos, FoxTheme.settings.moneyFormat);

  /** Property que leva a arte do logo, no vocabulário do Live Product Options. */
  const PROPRIEDADE_ARQUIVO_LOGO = 'Upload logo';

  /** Property do lado do peito, a mesma que a PDP grava (`bordado-pdp.js`). */
  const PROPRIEDADE_LADO = 'Lado do bordado';
  const PROPRIEDADE_LADO_ICONE = 'Lado do ícone';

  /** Property da peça com o NOME do ícone escolhido, a mesma que a PDP grava (ADR-0027). */
  const PROPRIEDADE_ICONE = 'Bordado ícone';

  /** Teto por arquivo de line item property, imposto pela Shopify. */
  const LIMITE_LOGO_MB = 20;

  /**
   * Substituto para o `JSON.stringify` que monta a chave de agrupamento. Um
   * `File` serializa como `{}`, então duas peças com artes diferentes cairiam na
   * mesma linha do carrinho. Artes iguais continuam agrupando, que é o certo:
   * mesma arte, mesma ordem de serviço.
   */
  const assinarArquivo = (_chave, valor) =>
    valor instanceof File ? `${valor.name}|${valor.size}|${valor.lastModified}` : valor;

  /**
   * Resolve um texto do locale, trocando os marcadores `{{ nome }}` pelos valores.
   * @param {Record<string, string>} textos
   * @param {string} chave
   * @param {Record<string, string|number>} valores
   * @returns {string}
   */
  function interpolar(textos, chave, valores) {
    return (textos[chave] ?? '').replace(/{{\s*(\w+)\s*}}/g, (_, nome) => valores[nome] ?? '');
  }

  /**
   * Calcula o que o cliente vê: quanto está cheio, quanto desconta e o total.
   *
   * Fica fora da classe para ser testável sem DOM. Os casos vivem em
   * `scripts/kit-builder-preco.test.mjs` e são os mesmos da Function, porque o
   * número da tela e o que a Shopify cobra têm de ser o mesmo — ADR-0018.
   *
   * O bordado de ícone (ADR-0027) entra em `icones`, à parte de `bordados`: soma no
   * cheio e no total, mas nunca na base do desconto, nem com `descontaBordado` —
   * espelha a Function, que exclui `_bordado_tipo: icone`. `bordados` no retorno é o
   * total de bordado mostrado na tela (texto, logo e ícone).
   *
   * @param {{precos: number[], bordados: number, icones?: number, grupos?: (number|string)[]}} kit Preços em centavos.
   *   `grupos` (só no combo) é paralelo a `precos`: o grupo, 1 ou 2, de cada unidade.
   * @param {object} config Dados da grade.
   * @returns {object}
   */
  function calcularKit(kit, config) {
    const unidades = kit.precos.length;
    const pecas = kit.precos.reduce((soma, preco) => soma + preco, 0);
    const icones = kit.icones ?? 0;
    const cheio = pecas + kit.bordados + icones;

    const base = {
      unidades,
      pecas,
      bordados: kit.bordados + icones,
      cheio,
      faixa: null,
      proxima: null,
      gratuitas: 0,
      faltam: 0,
      desconto: 0,
      total: cheio,
    };

    if (config.modo === 'leve-n') {
      // Espelha `regraLeveN` de app/extensions/desconto-de-kit/src/regras.js: grupo
      // não-inteiro degrada para "sem desconto", nunca arredonda por conta própria.
      const grupo = Number(config.grupo);
      if (!Number.isInteger(grupo) || grupo < 2) return base;

      const gratuitas = Math.floor(unidades / grupo);
      const faltam = gratuitas === 0 ? grupo - unidades : (grupo - (unidades % grupo)) % grupo;
      const desconto = config.descontoAtivo
        ? [...kit.precos]
            .sort((a, b) => a - b)
            .slice(0, gratuitas)
            .reduce((soma, preco) => soma + preco, 0)
        : 0;

      return { ...base, gratuitas, faltam, desconto, total: cheio - desconto };
    }

    if (config.modo === 'combo') return { ...base, ...regraCombo(kit, config, base) };

    const faixas = config.faixas ?? [];
    const faixa = config.descontoAtivo ? ([...faixas].reverse().find((f) => unidades >= f.quantidade) ?? null) : null;
    const proxima = faixas.find((f) => unidades < f.quantidade) ?? null;
    const descontavel = config.descontaBordado ? pecas + kit.bordados : pecas;
    const desconto = faixa ? Math.round((descontavel * faixa.percentual) / 100) : 0;

    return { ...base, faixa, proxima, desconto, total: cheio - desconto };
  }

  /**
   * Modo combo (ENN-37, vários pares em ADR-0024): espelha `regraCombo` de
   * app/extensions/desconto-de-kit/src/regras.js.
   *
   * Com `kit.grupos` (a grade nova sempre manda): pares = min(unidades do grupo 1,
   * unidades do grupo 2), e em cada grupo entram nos pares as unidades mais baratas;
   * o resto paga cheio. Sem nenhuma unidade com grupo, vale a regra antiga (a cada 2
   * unidades, na ordem da seleção) — mesma compatibilidade da Function. O bordado
   * nunca conta como peça, e configuração inválida degrada para "sem desconto".
   *
   * `config.combo` é o `{tipo, percentual}` ou `{tipo, preco}` (centavos) que o snippet emite.
   *
   * @param {{precos: number[], bordados: number, grupos?: (number|string)[]}} kit
   * @param {object} config
   * @param {{cheio: number}} base
   * @returns {{combos: number, desconto: number, total: number}}
   */
  function regraCombo(kit, config, base) {
    const dosCombos = unidadesDosPares(kit);
    const combos = dosCombos.length / 2;
    const semDesconto = { combos, desconto: 0, total: base.cheio };
    if (combos === 0 || !config.descontoAtivo || !config.combo) return semDesconto;

    const { tipo, percentual, preco } = config.combo;
    let desconto = 0;

    if (tipo === 'preco') {
      if (!(Number.isFinite(preco) && preco > 0)) return semDesconto;

      for (let i = 0; i < dosCombos.length; i += 2) {
        desconto += Math.max(0, dosCombos[i] + dosCombos[i + 1] - preco);
      }
    } else {
      if (!(Number.isFinite(percentual) && percentual > 0 && percentual <= 100)) return semDesconto;

      const soma = dosCombos.reduce((total, valor) => total + valor, 0);
      desconto = Math.round(((soma + (config.descontaBordado ? kit.bordados : 0)) * percentual) / 100);
    }

    return { combos, desconto, total: base.cheio - desconto };
  }

  /**
   * Preços das unidades que formam pares, em pares consecutivos [g1#1, g2#1, g1#2, ...].
   * Sem grupo em nenhuma unidade: as primeiras `2 * floor(n / 2)` (regra antiga).
   *
   * @param {{precos: number[], grupos?: (number|string)[]}} kit
   * @returns {number[]}
   */
  function unidadesDosPares({ precos, grupos }) {
    const doGrupo = (numero) => (grupos ?? []).map((grupo) => String(grupo) === String(numero));
    const temGrupo = doGrupo(1).some(Boolean) || doGrupo(2).some(Boolean);

    if (!temGrupo) return precos.slice(0, Math.floor(precos.length / 2) * 2);

    const maisBaratas = (numero) => {
      const marcas = doGrupo(numero);
      return precos.filter((_, indice) => marcas[indice]).sort((a, b) => a - b);
    };
    const g1 = maisBaratas(1);
    const g2 = maisBaratas(2);
    const pares = Math.min(g1.length, g2.length);

    return Array.from({ length: pares }, (_, i) => [g1[i], g2[i]]).flat();
  }

  /**
   * Quais unidades da seleção saem de graça — só para destacar no painel do modo lista
   * (ENN-6). Mesmo critério de `calcularKit` (as `gratuitas` unidades de menor preço),
   * aplicado aqui na camada de apresentação, sem tocar no cálculo em si: `calcularKit`
   * continua sendo a única fonte do que é cobrado.
   *
   * Em empate de preço, a ordem de desempate é estável (primeira unidade adicionada
   * vence) — não muda o valor cobrado, só qual das unidades idênticas aparece riscada.
   *
   * Fica fora da classe pelo mesmo motivo de `calcularKit`: testável sem DOM. Ver
   * `scripts/kit-builder-unidades-gratis.test.mjs`.
   *
   * @param {{preco: number}[]} selecao
   * @param {number} gratuitas Vem de `calcularKit(...).gratuitas`.
   * @returns {Set<number>} índices de `selecao` que saem de graça.
   */
  function indicesGratuitos(selecao, gratuitas) {
    if (!gratuitas) return new Set();

    return new Set(
      selecao
        .map((unidade, indice) => ({ indice, preco: unidade.preco }))
        .sort((a, b) => a.preco - b.preco)
        .slice(0, gratuitas)
        .map((par) => par.indice)
    );
  }

  /**
   * Line item properties da peça bordada do kit.
   *
   * Parte pura de `KitBuilder#linhasDoGrupo`, fora da classe para ser testável sem
   * DOM (`scripts/kit-builder-bordado.test.mjs`). Os nomes não são copy: precisam
   * bater com o que o Live Product Options grava na PDP individual, senão o pedido
   * sai com dois vocabulários.
   *
   * O lado só entra quando a calibragem oferece escolha (a tela só devolve `lado`
   * com 2 lados) — ausência significa o padrão de hoje, igual à PDP. Vai por
   * extenso, com o mesmo `rotuloDoLado` que a PDP usa.
   *
   * @param {{campos: Record<string,string>, cor: string, fonte: string, logo: boolean, lado?: string|null, icone?: string|null, texto?: boolean}} bordado
   *   `icone` é o nome do ícone; `texto: false` marca a peça sem bordado de texto, só ícone e/ou logo (ausente = com texto).
   * @param {{kit: string, posicao: string, fonteNome: string|null}} opcoes `fonteNome` é o
   *   nome da property de fonte no contrato do app, ou `null` se o produto não tem seletor.
   * @returns {Record<string, string>}
   */
  function propriedadesDaPeca(bordado, { kit, posicao, fonteNome }) {
    const props = { _kit: kit };

    Object.assign(props, bordado.campos, {
      'Adicione bordado': 'Sim',
      _kit_posicao: posicao,
    });

    // Sem texto (só ícone e/ou logo): cor de linha e campos em branco não viram property.
    if (bordado.cor) props.Cores = bordado.cor;
    if (bordado.texto === false) {
      Object.keys(bordado.campos ?? {}).forEach((nome) => {
        if (!String(props[nome] ?? '').trim()) delete props[nome];
      });
    }

    if (bordado.fonte && fonteNome) props[fonteNome] = bordado.fonte;
    if (bordado.lado) props[PROPRIEDADE_LADO] = FoxTheme.bordadoGeometria.rotuloDoLado(bordado.lado);
    if (bordado.logo) props['Bordado logo'] = 'Add';
    if (bordado.icone) props[PROPRIEDADE_ICONE] = bordado.icone;

    // Texto E ícone na mesma peça: o ícone vai no lado oposto ao do nome (ENN-92).
    const opostoDoIcone = FoxTheme.bordadoGeometria.ladoOposto(bordado.lado);
    if (bordado.icone && bordado.texto !== false && opostoDoIcone) {
      props[PROPRIEDADE_LADO_ICONE] = FoxTheme.bordadoGeometria.rotuloDoLado(opostoDoIcone);
    }

    return props;
  }

  /**
   * Linha de cobrança de bordado, amarrada à peça que personaliza.
   *
   * O `tipo` distingue as cobranças que a mesma peça pode ter — o bordado de
   * nome (sem tipo), o de logo e o de ícone. A guarda do carrinho precisa dele
   * para saber qual sumiu e corrigir só a metade certa da etiqueta, e a Function
   * do desconto lê `icone` para nunca descontá-lo (ADR-0027). Fica fora de
   * `_bordado_de` de propósito: é dessa property que a Function do ADR-0014
   * extrai o kit, e mudar o formato dela obrigaria a republicá-la.
   *
   * @param {number} varianteId
   * @param {number} quantidade
   * @param {string} referencia
   * @param {string} [tipo]
   * @returns {object}
   */
  function linhaDeBordado(varianteId, quantidade, referencia, tipo) {
    return {
      id: varianteId,
      quantity: quantidade,
      properties: { _bordado_de: referencia, ...(tipo ? { _bordado_tipo: tipo } : {}) },
    };
  }

  /**
   * Linhas de cobrança de uma peça bordada, na ordem da PDP: texto, logo, ícone.
   *
   * Parte pura de `KitBuilder#linhasDoGrupo`, testável sem DOM. Ícone sozinho (sem
   * texto, `texto: false`) não leva a linha `Bordado` de R$ 40,00 (ADR-0027); o logo
   * sozinho também não (D30). Ícone sem produto de cobrança é erro, nunca envio: sairia de graça.
   *
   * @param {{texto?: boolean, logo?: boolean, icone?: string|null}} bordado
   * @param {{bordado: {varianteId: number, logoVarianteId: number, iconeVarianteId?: number}, quantidade: number, referencia: string}} contexto
   * @returns {object[]}
   */
  function linhasDeCobranca(bordado, { bordado: produtos, quantidade, referencia }) {
    const cobrancas = [];

    if (bordado.texto !== false) cobrancas.push(linhaDeBordado(produtos.varianteId, quantidade, referencia));
    if (bordado.logo) cobrancas.push(linhaDeBordado(produtos.logoVarianteId, quantidade, referencia, 'logo'));

    if (bordado.icone) {
      if (!(produtos.iconeVarianteId > 0)) throw new Error('ícone marcado sem produto de cobrança do ícone');

      cobrancas.push(linhaDeBordado(produtos.iconeVarianteId, quantidade, referencia, 'icone'));
    }

    return cobrancas;
  }

  /**
   * Grade de kit. Guarda a seleção, calcula a faixa vigente e manda o carrinho.
   *
   * O estado é uma lista de unidades, não um mapa de quantidades: duas peças
   * iguais com bordados diferentes são duas entradas, porque personalização não
   * se compacta em quantidade.
   */
  class KitBuilder extends HTMLElement {
    #selecao = [];
    #filtro = 'todos';
    #focoPendente = null;
    #focoPendentePainel = null;
    #enviando = false;
    /** Tamanho escolhido por peça de várias variantes: handle → id da variante. Sem pré-seleção. */
    #tamanhos = new Map();
    /** Handle da peça cujo "adicionar" foi tentado sem tamanho (aviso inline), ou `null`. */
    #avisoTamanho = null;

    connectedCallback() {
      this.config = JSON.parse(this.querySelector('[data-kit-dados]').textContent);
      this.contrato = FoxTheme.bordadoContrato.lerContrato(window.Cloudlift);
      this.modal = this.querySelector('bordado-modal');
      this.listaDeIcones = this.#lerListaDeIcones();

      // A ordem dos blocks é do Theme Editor; a escada precisa ser crescente.
      this.config.faixas.sort((a, b) => a.quantidade - b.quantidade);

      // Sem contrato do LPO ou sem produto de cobrança, a grade não oferece bordado.
      const podeBordar = Boolean(this.contrato) && this.config.bordado.varianteId > 0;

      this.config.itens.forEach((item) => {
        item.aceitaBordado = podeBordar && this.contrato.handles.has(item.handle);
      });

      this.#renderizarEsqueleto();
      this.#atualizar();
    }

    /**
     * Texto do locale, já interpolado.
     * @param {string} chave
     * @param {Record<string, string|number>} [valores]
     * @returns {string}
     */
    #t(chave, valores = {}) {
      return interpolar(this.config.textos, chave, valores);
    }

    /**
     * Texto do locale que concorda em número com a quantidade.
     * @param {string} chave
     * @param {number} quantidade
     * @returns {string}
     */
    #tn(chave, quantidade) {
      return this.#t(`${chave}${quantidade === 1 ? '_one' : '_other'}`, { quantidade });
    }

    /** Modo combo (ENN-37): no mínimo 1 peça de cada grupo; sem teto (ADR-0024, vários pares). */
    #ehCombo() {
      return this.config.modo === 'combo';
    }

    /**
     * Títulos dos grupos do combo que ainda não têm peça escolhida.
     * @returns {string[]}
     */
    #gruposFaltando() {
      return (this.config.grupos ?? [])
        .map((grupo, indice) => ({ titulo: grupo.titulo, numero: indice + 1 }))
        // Grupo sem peça nenhuma (coleção vazia ou não configurada) não trava o botão.
        .filter(({ numero }) => this.config.itens.some((item) => item.grupo === numero))
        .filter(({ numero }) => !this.#selecao.some((unidade) => unidade.grupo === numero))
        .map(({ titulo }) => titulo);
    }

    /** Combo incompleto? Falso fora do modo combo. */
    #gruposFaltandoNoCombo() {
      return this.#ehCombo() && this.#gruposFaltando().length > 0;
    }

    /** Desenha as partes que não mudam com a seleção e liga os controles fixos. */
    #renderizarEsqueleto() {
      this.querySelector('[data-papel="corpo"]').innerHTML =
        this.config.layout === 'lista' ? this.#esqueletoLista() : this.#esqueletoGrade();

      this.$ = (papel) => this.querySelector(`[data-papel="${papel}"]`);

      // Vazio (NodeList) no modo lista, que não tem filtros — o forEach não faz nada.
      this.querySelectorAll('.kit-filtro').forEach((botao) =>
        botao.addEventListener('click', () => this.#aplicarFiltro(botao.dataset.filtro))
      );

      // "Limpar" não existe no modo lista (o Figma não tem) — guarda contra o nó ausente.
      this.$('limpar')?.addEventListener('click', () => {
        this.#selecao = [];
        this.#atualizar();
      });

      this.$('finalizar').addEventListener('click', () => this.#enviarAoCarrinho());
    }

    /** Esqueleto do modo padrão (grade de cartões) — inalterado desde antes da ENN-6. */
    #esqueletoGrade() {
      const escada = this.config.descontoAtivo
        ? '<div class="kit-escada" data-papel="escada"></div><p class="kit-proximo" data-papel="proximo"></p>'
        : '';

      return `
        ${escada}
        <ul class="kit-lista" data-papel="kit"></ul>
        <div class="kit-filtros">
          <button type="button" class="kit-filtro" data-filtro="todos" aria-pressed="true">${this.#t('filtro_todos')}</button>
          <button type="button" class="kit-filtro" data-filtro="bordavel" aria-pressed="false">${this.#t('filtro_bordavel')}</button>
          <button type="button" class="kit-filtro" data-filtro="nao-bordavel" aria-pressed="false">${this.#t('filtro_nao_bordavel')}</button>
          <span class="kit-contagem" data-papel="contagem"></span>
        </div>
        <div class="kit-grade" data-papel="grade"></div>
        <p class="kit-erro" data-papel="erro" role="alert"></p>
        <div class="kit-resumo">
          <div class="kit-resumo-info">
            <div class="kit-resumo-valores">
              <span class="kit-resumo-antigo" data-papel="antigo"></span>
              <span class="kit-resumo-total" data-papel="total"></span>
              <span class="kit-resumo-economia" data-papel="economia"></span>
            </div>
            <span class="kit-resumo-detalhe" data-papel="detalhe"></span>
          </div>
          <button type="button" class="btn btn--primary" data-papel="finalizar">${this.#t('finalizar')}</button>
          <button type="button" class="btn btn--plain" data-papel="limpar">${this.#t('limpar')}</button>
        </div>
      `;
    }

    /**
     * Esqueleto do modo lista (ENN-6, Figma novo): produtos em linha à esquerda,
     * painel "SEU KIT" fixo à direita. Sem filtros, sem contagem, sem barra de escada e
     * sem "Limpar" — o Figma não tem. O cabeçalho (eyebrow + título) é da section, não
     * daqui: `sections/kit-builder.liquid` o renderiza com `kit-titulo` antes deste
     * componente.
     *
     * Reaproveita os mesmos `data-papel` do modo grade sempre que o papel é o mesmo
     * (`grade`, `kit`, `erro`, `finalizar`, `escada`/`proximo`), para que o resto da
     * classe não precise saber em qual layout está.
     */
    #esqueletoLista() {
      const progresso = this.config.descontoAtivo
        ? '<p class="kit-painel-proximo" data-papel="proximo" aria-live="polite"></p>'
        : '';

      return `
        <div class="kit-lista-produtos" data-papel="grade"></div>
        <aside class="kit-painel" data-papel="painel">
          ${FoxTheme.kitPainel.htmlDoCabecalho({ titulo: this.#t('seu_kit'), logoUrl: this.config.logoUrl })}
          <div class="kit-painel-divisor"></div>
          <div data-papel="kit"></div>
          ${progresso}
          <div class="kit-painel-divisor"></div>
          <div class="kit-painel-totais" data-papel="totais" aria-live="polite"></div>
          <p class="kit-erro" data-papel="erro" role="alert"></p>
          <button type="button" class="kit-painel-botao" data-papel="finalizar">${this.#t('finalizar')}</button>
        </aside>
      `;
    }

    /** Desenha um card por peça do catálogo, respeitando o filtro ativo. */
    #renderizarGrade() {
      const visiveis = this.config.itens.filter((item) => this.#passaNoFiltro(item));

      this.$('grade').innerHTML = visiveis
        .map((item, indice) => {
          // No combo cada grupo abre com o próprio título; os cards seguem soltos no
          // mesmo container, então o layout dos dois modos não muda.
          const abreGrupo = this.#ehCombo() && (indice === 0 || item.grupo !== visiveis[indice - 1].grupo);
          const titulo = abreGrupo
            ? `<h3 class="kit-grupo-titulo">${this.config.grupos?.[item.grupo - 1]?.titulo ?? ''}</h3>`
            : '';

          return titulo + this.#cardDaPeca(item);
        })
        .join('');

      // Sem contagem no modo lista (o Figma não tem) — guarda contra o nó ausente.
      this.$('contagem') &&
        (this.$('contagem').textContent = this.#t('contagem', {
          visiveis: visiveis.length,
          total: this.config.itens.length,
        }));

      this.$('grade').querySelectorAll('[data-acao]').forEach((botao) => {
        const item = this.config.itens.find((i) => i.handle === botao.dataset.handle);

        const acoes = {
          bordado: () => !this.#faltaTamanho(item) && this.#pedirBordado(item),
          mais: () => !this.#faltaTamanho(item) && this.#adicionar(item, null),
          menos: () => this.#removerUnidade(item.handle),
          tamanho: () => this.#escolherTamanho(item, Number(botao.dataset.variante)),
        };

        botao.addEventListener('click', () => {
          this.#focoPendente =
            botao.dataset.acao === 'tamanho'
              ? `tamanho:${item.handle}:${botao.dataset.variante}`
              : `${botao.dataset.acao}:${item.handle}`;
          acoes[botao.dataset.acao]();
        });
      });

      this.#devolverFoco();
    }

    /**
     * Devolve o foco ao controle equivalente depois que a grade se redesenha.
     * Sem isto, usar o seletor de quantidade pelo teclado perde o foco a cada clique.
     */
    #devolverFoco() {
      if (!this.#focoPendente) return;

      const [acao, handle, variante] = this.#focoPendente.split(':');
      this.#focoPendente = null;

      // Chip de tamanho: volta ao mesmo chip (o redesenho recria os botões).
      if (acao === 'tamanho') {
        this.$('grade').querySelector(`[data-acao="tamanho"][data-handle="${handle}"][data-variante="${variante}"]`)?.focus();
        return;
      }

      // `focus()` num botão desabilitado joga o foco no body — se o controle de antes
      // sumiu ou desabilitou, cai no "−" da mesma peça.
      const grade = this.$('grade');
      (
        grade.querySelector(`[data-acao="${acao}"][data-handle="${handle}"]:not([disabled])`) ??
        grade.querySelector(`[data-acao="menos"][data-handle="${handle}"]`)
      )?.focus();
    }

    /**
     * Variante escolhida de uma peça de várias variantes, ou `null` (peça de variante
     * única, ou tamanho ainda não escolhido).
     * @param {object} item
     * @returns {{id: number, titulo: string, preco: number, disponivel: boolean}|null}
     */
    #varianteEscolhida(item) {
      const id = this.#tamanhos.get(item.handle);
      return item.variantes?.find((v) => v.id === id && v.disponivel) ?? null;
    }

    /**
     * Unidade pronta para a seleção: a peça com a variante e o preço do tamanho escolhido.
     * Peça de variante única sai como sempre saiu (`{...item, bordado}`).
     * @param {object} item
     * @param {object|null} bordado
     * @returns {object}
     */
    #unidadeDe(item, bordado) {
      const variante = this.#varianteEscolhida(item);
      return variante
        ? { ...item, varianteId: variante.id, preco: variante.preco, tamanho: variante.titulo, bordado }
        : { ...item, bordado };
    }

    /**
     * Peça de várias variantes sem tamanho escolhido: mostra o aviso inline e devolve
     * `true`, para quem chama não adicionar nada.
     * @param {object} item
     * @returns {boolean}
     */
    #faltaTamanho(item) {
      if (!item.variantes || this.#varianteEscolhida(item)) return false;

      this.#avisoTamanho = item.handle;
      this.#atualizar();
      return true;
    }

    /**
     * Escolhe o tamanho de uma peça. Vale para as próximas unidades adicionadas; as que
     * já estão no kit mantêm o tamanho com que entraram (no combo também: ele aceita
     * várias unidades da mesma peça, ADR-0024).
     * @param {object} item
     * @param {number} varianteId
     */
    #escolherTamanho(item, varianteId) {
      const variante = item.variantes?.find((v) => v.id === varianteId);
      if (!variante?.disponivel) return;

      this.#tamanhos.set(item.handle, variante.id);
      if (this.#avisoTamanho === item.handle) this.#avisoTamanho = null;

      this.#atualizar();
    }

    /**
     * Preço mostrado no cartão: o do tamanho escolhido, senão o da peça (o "a partir de").
     * @param {object} item
     * @returns {number} centavos
     */
    #precoExibido(item) {
      return this.#varianteEscolhida(item)?.preco ?? item.preco;
    }

    /**
     * Nome da unidade nas listas do kit: com o tamanho, quando a peça tem.
     * @param {object} unidade
     * @returns {string}
     */
    #nomeDaUnidade(unidade) {
      return unidade.tamanho ? `${unidade.titulo} · ${unidade.tamanho}` : unidade.titulo;
    }

    /**
     * Fileira de chips de tamanho de uma peça de várias variantes (vazia nas demais).
     * Tamanho esgotado é chip desabilitado; nenhum vem pré-selecionado.
     * @param {object} item
     * @returns {string}
     */
    #tamanhosDaPeca(item) {
      if (!item.variantes) return '';

      const escolhido = this.#tamanhos.get(item.handle);
      const chips = item.variantes
        .map((v) => {
          const esgotado = v.disponivel ? '' : ' disabled aria-disabled="true"';
          return `<button type="button" class="kit-tamanho" data-acao="tamanho" data-handle="${item.handle}" data-variante="${v.id}" aria-pressed="${v.id === escolhido && v.disponivel}"${esgotado}>${v.titulo}</button>`;
        })
        .join('');
      const aviso =
        this.#avisoTamanho === item.handle
          ? `<p class="kit-tamanho-aviso" role="alert">${this.#t('escolha_tamanho')}</p>`
          : '';

      return `<div class="kit-tamanhos" role="group" aria-label="${this.#t('tamanho')}">${chips}</div>${aviso}`;
    }

    /**
     * Diz se uma peça deve aparecer com o filtro atual.
     * @param {object} item
     * @returns {boolean}
     */
    #passaNoFiltro(item) {
      if (this.#filtro === 'bordavel') return item.aceitaBordado;
      if (this.#filtro === 'nao-bordavel') return !item.aceitaBordado;
      return true;
    }

    /**
     * Markup de um card/linha da grade, no layout ativo.
     * @param {object} item
     * @returns {string}
     */
    #cardDaPeca(item) {
      return this.config.layout === 'lista' ? this.#linhaDaPeca(item) : this.#cardDaPecaGrade(item);
    }

    /**
     * Markup de um card da grade — modo padrão, inalterado desde antes da ENN-6.
     * @param {object} item
     * @returns {string}
     */
    #cardDaPecaGrade(item) {
      const noKit = this.#selecao.filter((unidade) => unidade.handle === item.handle).length;

      const entrada = noKit
        ? `<div class="kit-quantidade">
             ${this.#passo(item, 'menos', '−', 'remover_unidade')}
             <span class="kit-valor" aria-live="polite">${noKit}</span>
             ${this.#passo(item, 'mais', '+', 'adicionar_unidade')}
           </div>`
        : `<button type="button" class="btn btn--primary" data-acao="mais" data-handle="${item.handle}">${this.#t('adicionar')}</button>`;

      const bordado = item.aceitaBordado
        ? `<button type="button" class="btn btn--secondary" data-acao="bordado" data-handle="${item.handle}">${this.#t('adicionar_bordado')}</button>`
        : `<span class="kit-sem-bordado">${this.#t('nao_aceita_bordado')}</span>`;

      return `
        <article class="kit-peca${item.disponivel ? '' : ' kit-esgotado'}" data-selecionado="${noKit ? 'sim' : 'nao'}">
          <figure><img src="${item.imagem}" alt="${item.titulo}" loading="lazy" width="400" height="400"></figure>
          <div class="kit-peca-corpo">
            <h3 class="kit-peca-titulo">${item.titulo}</h3>
            <p class="kit-peca-preco">${emDinheiro(this.#precoExibido(item))}</p>
            ${item.disponivel ? this.#tamanhosDaPeca(item) : ''}
            <div class="kit-peca-acoes">
              ${item.disponivel ? entrada : `<span class="kit-sem-bordado">${this.#t('esgotado')}</span>`}
              ${item.disponivel ? bordado : ''}
            </div>
          </div>
        </article>
      `;
    }

    /**
     * Markup de uma linha da grade — modo lista (ENN-6). O "+" sempre adiciona a
     * unidade sem bordado (`data-acao="mais"`), mesmo em peça bordável — decisão do
     * Lucas em 24/09/2026: o bordado se pede depois, pelo painel "SEU KIT"
     * (`#adicionarBordadoNaUnidade`), nunca pelo "+". O stepper fica sempre visível,
     * inclusive em zero, com o "−" desabilitado nesse caso.
     *
     * @param {object} item
     * @returns {string}
     */
    #linhaDaPeca(item) {
      const noKit = this.#selecao.filter((unidade) => unidade.handle === item.handle).length;

      const stepper = `
        <div class="kit-linha-stepper">
          ${this.#passo(item, 'menos', '−', 'remover_unidade', { classe: 'kit-linha-passo', desabilitado: noKit === 0 })}
          <span class="kit-linha-qtd" aria-live="polite">${noKit}</span>
          ${this.#passo(item, 'mais', '+', 'adicionar_unidade', { classe: 'kit-linha-passo' })}
        </div>
      `;

      return `
        <article class="kit-linha${item.disponivel ? '' : ' kit-linha--esgotada'}" data-selecionado="${noKit ? 'sim' : 'nao'}">
          <img class="kit-linha-img" src="${item.imagem}" alt="${item.titulo}" loading="lazy" width="93" height="99">
          <span class="kit-linha-nome">${item.titulo}</span>
          <span class="kit-linha-preco">${emDinheiro(this.#precoExibido(item))}</span>
          ${item.disponivel ? stepper : `<span class="kit-sem-bordado">${this.#t('esgotado')}</span>`}
          ${item.disponivel && item.variantes ? `<div class="kit-linha-tamanhos">${this.#tamanhosDaPeca(item)}</div>` : ''}
        </article>
      `;
    }

    /**
     * Um dos dois botões do seletor de quantidade. Compartilhado pelos dois layouts:
     * a classe e o `disabled` variam, a ação e o rótulo acessível não.
     * @param {object} item
     * @param {string} acao
     * @param {string} sinal
     * @param {string} chaveDoRotulo
     * @param {{classe?: string, desabilitado?: boolean}} [opcoes]
     * @returns {string}
     */
    #passo(item, acao, sinal, chaveDoRotulo, { classe = 'kit-passo', desabilitado = false } = {}) {
      const rotulo = this.#t(chaveDoRotulo, { produto: item.titulo });

      return `<button type="button" class="${classe}" data-acao="${acao}" data-handle="${item.handle}" aria-label="${rotulo}"${desabilitado ? ' disabled' : ''}>${sinal}</button>`;
    }

    /**
     * Troca o filtro e redesenha a grade.
     * @param {string} filtro
     */
    #aplicarFiltro(filtro) {
      this.#filtro = filtro;

      this.querySelectorAll('.kit-filtro').forEach((botao) =>
        botao.setAttribute('aria-pressed', String(botao.dataset.filtro === filtro))
      );

      this.#renderizarGrade();
    }

    /**
     * Abre o modal de bordado e adiciona a peça se ele for confirmado. Só o modo grade
     * usa este caminho (o "+" da peça bordável) — no modo lista o "+" sempre adiciona
     * sem bordado, e o bordado se pede depois pelo painel, ver
     * `#adicionarBordadoNaUnidade`.
     * @param {object} item
     */
    async #pedirBordado(item) {
      const bordado = await this.#abrirModal(item);
      if (bordado) this.#adicionar(item, bordado);
    }

    /**
     * Adiciona bordado a uma unidade já escolhida, pelo botão "Adicionar bordado
     * personalizado" do painel "SEU KIT" (modo lista, decisão do Lucas em 24/09/2026).
     * Confirmado, substitui a entrada no mesmo índice de `#selecao` — não cria unidade
     * nova, não muda a quantidade. Cancelado, nada muda.
     * @param {number} indice
     */
    async #adicionarBordadoNaUnidade(indice) {
      const unidade = this.#selecao[indice];
      if (!unidade) return;

      const bordado = await this.#abrirModal(unidade, { tamanhoFixo: Boolean(unidade.tamanho) });
      if (!bordado) return;

      this.#selecao = this.#selecao.map((u, i) => (i === indice ? { ...u, bordado } : u));
      this.#atualizar();
    }

    /**
     * Põe uma unidade no kit.
     * @param {object} item
     * @param {object|null} bordado
     */
    #adicionar(item, bordado) {
      // Combo: sem teto por grupo, escolher outra peça soma à anterior (ADR-0024). A
      // seleção fica na ordem dos grupos (o sort é estável: dentro do grupo, a ordem
      // em que foram adicionadas).
      this.#selecao = [...this.#selecao, this.#unidadeDe(item, bordado)];
      if (this.#ehCombo()) this.#selecao.sort((a, b) => a.grupo - b.grupo);
      this.#atualizar();
    }

    /**
     * Tira uma unidade do kit pelo índice.
     * @param {number} indice
     */
    #remover(indice) {
      this.#selecao = this.#selecao.filter((_, i) => i !== indice);
      this.#atualizar();
    }

    /**
     * Tira uma unidade de uma peça pelo seletor de quantidade do card.
     *
     * Tira sempre a última unidade **sem bordado** primeiro: uma personalização
     * preenchida à mão não pode sumir por causa de um clique no menos. Para
     * remover uma bordada existe o × explícito na lista do kit.
     *
     * @param {string} handle
     */
    #removerUnidade(handle) {
      const daPeca = (unidade) => unidade.handle === handle;
      const semBordado = this.#selecao.findLastIndex((u) => daPeca(u) && !u.bordado);

      this.#remover(semBordado === -1 ? this.#selecao.findLastIndex(daPeca) : semBordado);
    }

    /** Recalcula tudo que depende da seleção e redesenha as partes vivas. */
    #atualizar() {
      const conta = this.#calcular();

      if (this.config.descontoAtivo) this.#renderizarEscada(conta);
      this.#renderizarKit(conta);
      this.#renderizarGrade();
      this.#renderizarResumo(conta);
    }

    /**
     * Núcleo da regra comercial: monta os preços da seleção e delega a `calcularKit`.
     *
     * O cálculo em si vive fora da classe, testável sem DOM — ver
     * `scripts/kit-builder-preco.test.mjs`.
     *
     * @returns {ReturnType<typeof calcularKit>}
     */
    #calcular() {
      const precos = this.#selecao.map((unidade) => unidade.preco);
      const bordados = this.#selecao.reduce((soma, unidade) => soma + this.#precoDoBordado(unidade.bordado), 0);
      const icones = this.#selecao.reduce((soma, unidade) => soma + this.#precoDoIcone(unidade.bordado), 0);

      // `grupos` só pesa no combo; nos outros modos o `item.grupo` nem existe.
      const grupos = this.#ehCombo() ? this.#selecao.map((unidade) => unidade.grupo) : undefined;

      return calcularKit({ precos, bordados, icones, grupos }, this.config);
    }

    /**
     * Preço do bordado de uma unidade, somando nome/especialidade e logo. Sem
     * texto (só logo e/ou ícone) não paga a linha de texto (ADR-0027, D30). O ícone fica de fora: ver
     * `#precoDoIcone`.
     * @param {object|null} bordado
     * @returns {number} centavos
     */
    #precoDoBordado(bordado) {
      if (!bordado) return 0;

      return (bordado.texto === false ? 0 : this.config.bordado.preco) + (bordado.logo ? this.config.bordado.logoPreco : 0);
    }

    /**
     * Preço do bordado de ícone de uma unidade. À parte do resto porque nunca entra
     * na base do desconto de kit (ADR-0027).
     * @param {object|null} bordado
     * @returns {number} centavos
     */
    #precoDoIcone(bordado) {
      return bordado?.icone ? (this.config.bordado.iconePreco ?? 0) : 0;
    }

    /** Preço total do bordado da unidade (texto, logo e ícone), para exibição. */
    #precoTotalDoBordado(bordado) {
      return this.#precoDoBordado(bordado) + this.#precoDoIcone(bordado);
    }

    /**
     * Pinta a escada de faixas, marcando a vigente.
     * @param {ReturnType<typeof calcularKit>} calculo
     */
    #renderizarEscada(calculo) {
      if (this.config.modo === 'leve-n') return this.#renderizarProgressoDeGratis(calculo);

      // Combo não tem escada nem progresso: o que falta aparece no botão de finalizar.
      if (this.#ehCombo()) {
        this.$('escada') && (this.$('escada').innerHTML = '');
        this.$('proximo') && (this.$('proximo').innerHTML = '');
        return;
      }

      const { unidades, faixa, proxima } = calculo;

      // O modo lista não tem barra de escada (o Figma não tem) — guarda contra o nó ausente.
      const escada = this.$('escada');
      if (escada) {
        escada.innerHTML = this.config.faixas
          .map((f, indice) => {
            const ultima = indice === this.config.faixas.length - 1;
            const estado =
              faixa && f.quantidade === faixa.quantidade
                ? 'ativo'
                : unidades >= f.quantidade
                  ? 'alcancado'
                  : 'pendente';

            return `
              <div class="kit-degrau" data-estado="${estado}">
                <b>${this.#t('com_desconto', { percentual: f.percentual })}</b>
                <span>${this.#t(f.quantidade === 1 ? 'degrau_one' : 'degrau_other', { quantidade: `${f.quantidade}${ultima ? '+' : ''}` })}</span>
              </div>
            `;
          })
          .join('');
      }

      const faltam = proxima ? proxima.quantidade - unidades : 0;

      this.$('proximo').innerHTML = proxima
        ? this.#t(faltam === 1 ? 'faltam_one_html' : 'faltam_other_html', {
            quantidade: faltam,
            percentual: proxima.percentual,
          })
        : faixa
          ? this.#t('desconto_maximo_html', { percentual: faixa.percentual })
          : '';
    }

    /**
     * No modo "a cada N peças" não há escada de degraus: o que o cliente precisa
     * saber é quantas peças já saem de graça e quanto falta para a próxima.
     *
     * @param {{gratuitas: number, faltam: number}} calculo
     */
    #renderizarProgressoDeGratis({ gratuitas, faltam }) {
      // O modo lista não tem barra de escada (o Figma não tem) — guarda contra o nó ausente.
      this.$('escada') && (this.$('escada').innerHTML = '');

      // No modo lista a linha de desconto do painel já diz "N peças grátis"; repetir aqui
      // duplicava o texto logo acima do subtotal.
      const repeteODesconto = this.config.layout === 'lista';

      this.$('proximo').innerHTML = faltam
        ? this.#t(faltam === 1 ? 'faltam_gratis_one_html' : 'faltam_gratis_other_html', { quantidade: faltam })
        : gratuitas && !repeteODesconto
          ? this.#tn('gratis', gratuitas)
          : '';
    }

    /**
     * Lista as unidades escolhidas, no layout ativo.
     * @param {ReturnType<typeof calcularKit>} conta Só usado pelo modo lista, para
     *   destacar as unidades gratuitas — ver `indicesGratuitos`.
     */
    #renderizarKit(conta) {
      return this.config.layout === 'lista' ? this.#renderizarKitLista(conta) : this.#renderizarKitGrade();
    }

    /** Modo padrão: cada unidade com seu bordado e seu botão de remover. */
    #renderizarKitGrade() {
      if (this.#selecao.length === 0) {
        this.$('kit').innerHTML = `<li class="kit-vazio">${this.config.textoVazio}</li>`;
        return;
      }

      this.$('kit').innerHTML = this.#selecao
        .map(
          (unidade, indice) => `
            <li class="kit-item">
              <img src="${unidade.imagem}" alt="" width="40" height="40" loading="lazy">
              <span class="kit-item-texto">
                <span class="kit-item-nome">${this.#nomeDaUnidade(unidade)}</span>
                <span class="kit-item-bordado">${this.#descreverBordado(unidade.bordado)}</span>
              </span>
              <span class="kit-item-preco">${emDinheiro(unidade.preco + this.#precoTotalDoBordado(unidade.bordado))}</span>
              <button type="button" class="btn btn--plain" data-remover="${indice}" aria-label="${this.#t('remover_do_kit', { produto: this.#nomeDaUnidade(unidade) })}">×</button>
            </li>
          `
        )
        .join('');

      this.#ligarRemocao(this.$('kit'));
    }

    /**
     * Modo lista (ENN-6): rótulo "Toucas" + lista, com as unidades gratuitas (as
     * `gratuitas` mais baratas, só quando `descontoAtivo` — mesma invariante do resto da
     * tela) riscadas e mostrando `emDinheiro(0)`. Vazio, mostra `textoVazio` sem rótulo.
     *
     * Cada unidade sem bordado, de peça que aceita bordado, ganha o botão "Adicionar
     * bordado personalizado por +valor" — decisão do Lucas em 24/09/2026: o "+" da grade
     * nunca abre o modal, o bordado se pede daqui (`#adicionarBordadoNaUnidade`). Unidade
     * já bordada mostra o detalhe do bordado, como sempre; não existe "editar"/"remover
     * bordado" (fora do Figma) — quem quiser tirar remove a unidade inteira, pelo × de
     * sempre.
     *
     * @param {ReturnType<typeof calcularKit>} conta
     */
    #renderizarKitLista(conta) {
      const destino = this.$('kit');

      if (this.#selecao.length === 0) {
        destino.innerHTML = `<p class="kit-painel-vazio">${this.config.textoVazio}</p>`;
        return;
      }

      const gratuitas = this.config.descontoAtivo ? (conta?.gratuitas ?? 0) : 0;
      const gratis = indicesGratuitos(this.#selecao, gratuitas);

      const contexto = this.#contextoDoPainel();

      const itemDoPainel = (unidade, indice) =>
        FoxTheme.kitPainel.htmlDoItem(
          {
            nome: this.#nomeDaUnidade(unidade),
            preco: unidade.preco,
            bordado: unidade.bordado,
            descricaoDoBordado: unidade.bordado ? this.#descreverBordado(unidade.bordado) : '',
            precoDoBordado: this.#precoTotalDoBordado(unidade.bordado),
            aceitaBordado: unidade.aceitaBordado,
            gratis: gratis.has(indice),
          },
          indice,
          contexto
        );

      if (this.#ehCombo()) {
        // Um bloco por grupo, com o título dele: a peça escolhida ou o que falta.
        destino.innerHTML = (this.config.grupos ?? [])
          .map((grupo, numero) => {
            const itens = this.#selecao
              .map((unidade, indice) => ({ unidade, indice }))
              .filter(({ unidade }) => unidade.grupo === numero + 1);
            const conteudo = itens.length
              ? `<ul class="kit-painel-lista">${itens.map(({ unidade, indice }) => itemDoPainel(unidade, indice)).join('')}</ul>`
              : `<p class="kit-painel-vazio">${this.#t('falta_escolher', { grupo: grupo.titulo })}</p>`;

            return `<p class="kit-painel-rotulo">${grupo.titulo}</p>${conteudo}`;
          })
          .join('');
      } else {
        destino.innerHTML = `
          <p class="kit-painel-rotulo">${this.#t('toucas')}</p>
          <ul class="kit-painel-lista">
            ${this.#selecao.map(itemDoPainel).join('')}
          </ul>
        `;
      }

      this.#ligarRemocao(destino);
      this.#ligarBordadoDoPainel(destino);
      this.#devolverFocoPainel();
    }

    /**
     * Liga o clique de todos os botões de remover unidade dentro de `destino`.
     * Compartilhado pelos dois layouts.
     * @param {HTMLElement} destino
     */
    #ligarRemocao(destino) {
      destino
        .querySelectorAll('[data-remover]')
        .forEach((botao) =>
          botao.addEventListener('click', () => this.#remover(Number(botao.dataset.remover)))
        );
    }

    /**
     * Liga o clique dos botões "Adicionar bordado personalizado" do painel (modo lista).
     * @param {HTMLElement} destino
     */
    #ligarBordadoDoPainel(destino) {
      destino.querySelectorAll('[data-bordar]').forEach((botao) => {
        const indice = Number(botao.dataset.bordar);

        botao.addEventListener('click', () => {
          // O botão some depois de confirmado (a unidade passa a mostrar o detalhe do
          // bordado); o × da mesma linha é o controle equivalente mais próximo.
          this.#focoPendentePainel = `[data-remover="${indice}"]`;
          this.#adicionarBordadoNaUnidade(indice);
        });
      });
    }

    /**
     * Devolve o foco a um controle equivalente do painel depois que ele se redesenha —
     * mesmo padrão de `#devolverFoco`, para a grade.
     */
    #devolverFocoPainel() {
      if (!this.#focoPendentePainel) return;

      const seletor = this.#focoPendentePainel;
      this.#focoPendentePainel = null;

      this.$('kit')?.querySelector(seletor)?.focus();
    }

    /**
     * Frase curta que descreve o bordado de uma unidade na lista do kit.
     * @param {object|null} bordado
     * @returns {string}
     */
    #descreverBordado(bordado) {
      if (!bordado) return this.#t('sem_bordado');

      const partes = [
        ...Object.values(bordado.campos),
        bordado.cor,
        bordado.fonte,
        bordado.logo ? this.#t('com_logo') : null,
        bordado.icone,
      ];

      return this.#t('bordado', { detalhe: partes.filter(Boolean).join(' · ') });
    }

    /**
     * Contexto do painel compartilhado (`assets/kit-painel.js`): textos, moeda e os
     * preços de cobrança da grade. A grade tem o × e não tem Editar/Remover do bordado.
     *
     * O preço e a descrição do bordado de cada peça continuam sendo calculados aqui
     * (`#precoTotalDoBordado`, `#descreverBordado`) e entram prontos no painel: a grade
     * não depende de `kit-painel.js` para o preço, só para o desenho do painel.
     * `kit-painel.js` tem a mesma conta para a oferta da PDP, e
     * `scripts/kit-painel.test.mjs` confere que as duas dão o mesmo número e o mesmo texto.
     */
    #contextoDoPainel() {
      return {
        t: (chave, valores) => this.#t(chave, valores),
        dinheiro: emDinheiro,
        precos: this.config.bordado,
        remover: true,
        acoesDoBordado: false,
      };
    }

    /**
     * Atualiza a barra/painel de total, no layout ativo. O botão de finalizar é
     * desabilitado aqui, fora dos dois ramos: a regra (kit vazio ou envio em curso) é a
     * mesma nos dois layouts.
     * @param {ReturnType<typeof calcularKit>} conta
     */
    #renderizarResumo(conta) {
      if (this.config.layout === 'lista') this.#renderizarResumoLista(conta);
      else this.#renderizarResumoGrade(conta);

      // Combo: só finaliza com 1 peça de cada grupo, e o botão diz o que falta.
      const faltando = this.#ehCombo() ? this.#gruposFaltando() : [];
      const finalizar = this.$('finalizar');

      if (this.#ehCombo()) {
        finalizar.textContent = faltando.length ? this.#t('falta_escolher', { grupo: faltando[0] }) : this.#t('finalizar');
      }

      finalizar.disabled = conta.unidades === 0 || faltando.length > 0 || this.#enviando;
    }

    /**
     * Modo padrão: barra de total inalterada desde antes da ENN-6.
     * @param {object} conta
     */
    #renderizarResumoGrade({ unidades, bordados, faixa, cheio, desconto, total, gratuitas }) {
      this.$('total').textContent = emDinheiro(total);
      this.$('antigo').textContent = desconto ? emDinheiro(cheio) : '';
      this.$('economia').textContent = desconto ? this.#t('economia', { valor: emDinheiro(desconto) }) : '';

      const partes = [
        this.#tn('pecas', unidades),
        bordados ? this.#t('em_bordado', { valor: emDinheiro(bordados) }) : null,
        // Invariante: a tela nunca anuncia desconto/peça grátis que a loja não
        // cobra — os dois ramos (escada e leve-n) ficam sob descontoAtivo.
        this.config.descontoAtivo && !this.#ehCombo()
          ? this.config.modo === 'leve-n'
            ? gratuitas
              ? this.#tn('gratis', gratuitas)
              : null
            : faixa
              ? this.#t('com_desconto', { percentual: faixa.percentual })
              : this.#t('sem_desconto')
          : null,
      ].filter(Boolean);

      this.$('detalhe').textContent = unidades ? partes.join(' · ') : this.#t('kit_vazio');
    }

    /**
     * Modo lista (ENN-6): "Subtotal" sempre visível (o cheio, sem desconto), a linha de
     * desconto/peças grátis e a de bordado só quando existem, e "Total" em destaque.
     * Mesma invariante do resto da tela: desconto/gratuidade só aparecem sob
     * `descontoAtivo` — a tela nunca anuncia o que a loja não cobra.
     * @param {object} conta
     */
    #renderizarResumoLista({ bordados, cheio, desconto, total, gratuitas }) {
      const comDesconto = this.config.descontoAtivo && desconto > 0;

      this.$('totais').innerHTML = FoxTheme.kitPainel.htmlDosTotais(
        {
          cheio,
          desconto,
          bordados,
          total,
          rotuloDesconto: comDesconto ? (this.#ehCombo() ? this.#t('desconto_combo') : this.#tn('gratis', gratuitas)) : '',
        },
        this.#contextoDoPainel()
      );
    }

    /**
     * Manda a seleção ao carrinho e avisa o tema, para a gaveta se atualizar
     * como em qualquer outro botão.
     *
     * Quase tudo cabe numa requisição só. A exceção é a peça que leva arte de
     * logo: arquivo em line item property exige multipart, e multipart carrega
     * um item por chamada. Essas vão uma a uma, antes do lote.
     */
    async #enviarAoCarrinho() {
      if (this.#enviando || this.#selecao.length === 0 || this.#gruposFaltandoNoCombo()) return;

      this.#enviando = true;
      this.$('erro').textContent = '';
      this.$('finalizar').setAttribute('aria-busy', 'true');

      // Envio em mais de uma chamada passa por estados que a guarda do carrinho
      // lê como divergência de verdade — a peça personalizada já no carrinho, a
      // linha que a cobra ainda não. Ela espera o fim e reconcilia uma vez, sobre
      // o resultado inteiro. Se algo estourar no meio, o `finally` a solta e é
      // ela quem limpa o que ficou pela metade.
      document.dispatchEvent(new CustomEvent('kit:envio-iniciado'));

      try {
        const { comArte, linhas } = this.#montarPayload();
        const conferencia = FoxTheme.kitConferencia;

        // Cada chamada responder sem erro não prova que a peça está no carrinho
        // (ENN-96): a conferência lê antes e depois, reenvia uma vez o que faltar
        // e, se ainda faltar, desfaz o envio inteiro e lança.
        const { ultima } = await conferencia.enviarConferindo({
          comArte,
          linhas,
          origem: 'grade',
          enviarComArte: (linha, arquivo) => this.#enviarComArte(linha, arquivo),
          enviarLinhas: (lote) => this.#enviarLinhas(lote),
          lerCarrinho: () => conferencia.lerItensDoCarrinho(),
          reverter: (atualizacoes) => conferencia.reverterNoCarrinho(atualizacoes),
        });

        const carrinho = await this.#lerCarrinhoComSections();

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: carrinho });
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: ultima } }));

        this.#selecao = [];
      } catch (erro) {
        // A seleção fica: o envio foi desfeito e a cliente pode tentar de novo.
        this.#mostrarErro(
          erro.codigo === 'kit_envio_divergente'
            ? this.#mensagemDePecasFaltando(erro.pecas)
            : this.#t('erro', { mensagem: erro.message })
        );
      } finally {
        document.dispatchEvent(new CustomEvent('kit:envio-concluido'));
        this.#enviando = false;
        this.$('finalizar').removeAttribute('aria-busy');
        this.#atualizar();
      }
    }

    /**
     * Texto de erro quando o envio foi desfeito por peças que não entraram. Dá o
     * nome da peça e do tamanho, nunca o texto do bordado.
     * @param {{linha: {id: number|string}, quantidade: number}[]} pecas
     * @returns {string}
     */
    #mensagemDePecasFaltando(pecas) {
      const nomes = new Map(this.#selecao.map((unidade) => [String(unidade.varianteId), this.#nomeDaUnidade(unidade)]));
      const total = pecas.reduce((soma, { quantidade }) => soma + quantidade, 0);
      const lista = pecas
        .map(({ linha, quantidade }) => {
          const nome = nomes.get(String(linha.id)) ?? this.#t('peca_generica');
          return quantidade > 1 ? `${quantidade}× ${nome}` : nome;
        })
        .join(', ');

      return this.#t(total === 1 ? 'envio_incompleto_one' : 'envio_incompleto_other', {
        quantidade: total,
        pecas: lista,
      });
    }

    /**
     * Mostra o erro e o traz para a tela se estiver fora da viewport: no celular
     * o botão e o erro ficam no fim de uma grade longa.
     * @param {string} mensagem
     */
    #mostrarErro(mensagem) {
      const erro = this.$('erro');

      erro.textContent = mensagem;
      erro.scrollIntoView?.({ block: 'nearest' });
    }

    /**
     * Busca o carrinho já com o HTML das sections que escutam `cartUpdate`.
     *
     * Quem assina o evento lê `cart.sections[id]` sem guarda — ver
     * `assets/cart.js`, no `onCartUpdate` da gaveta. Carrinho vindo de
     * `/cart.js` não traz `sections`, e a gaveta estourava depois de o kit já
     * ter entrado: o cliente via erro numa operação que deu certo.
     *
     * `/cart/update.js` com `updates` vazio não altera nada e devolve o
     * carrinho inteiro mais as sections pedidas, numa requisição só. Os ids
     * saem do mesmo evento que o resto do tema usa, então a grade não precisa
     * saber quais sections existem na página.
     *
     * @returns {Promise<object>}
     */
    async #lerCarrinhoComSections() {
      const sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
      );

      return (
        await fetch(FoxTheme.routes.cart_update_url, {
          ...FoxTheme.utils.fetchConfig(),
          body: JSON.stringify({ updates: {}, sections, sections_url: window.location.pathname }),
        })
      ).json();
    }

    /**
     * Adiciona de uma vez todas as linhas que não levam arquivo.
     * @param {object[]} linhas
     * @returns {Promise<object>}
     */
    async #enviarLinhas(linhas) {
      return this.#lerResposta(
        await fetch(FoxTheme.routes.cart_add_url, {
          ...FoxTheme.utils.fetchConfig('javascript'),
          body: JSON.stringify({ items: linhas }),
        })
      );
    }

    /**
     * Adiciona uma peça junto com a arte do logo.
     *
     * Sem `Content-Type` de propósito: quem escreve o boundary do multipart é o
     * navegador, e declarar o cabeçalho à mão deixa o corpo sem boundary e a
     * Shopify sem como separar os campos.
     *
     * @param {object} linha
     * @param {File} arquivo
     * @returns {Promise<object>}
     */
    async #enviarComArte(linha, arquivo) {
      const corpo = new FormData();

      corpo.append('id', linha.id);
      corpo.append('quantity', linha.quantity);
      Object.entries(linha.properties).forEach(([nome, valor]) => corpo.append(`properties[${nome}]`, valor));
      corpo.append(`properties[${PROPRIEDADE_ARQUIVO_LOGO}]`, arquivo);

      return this.#lerResposta(
        await fetch(FoxTheme.routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: corpo,
        })
      );
    }

    /**
     * Lê a resposta do `/cart/add.js`. O erro da Shopify vem descrito no corpo,
     * não como falha de rede, então vira exceção aqui.
     * @param {Response} resposta
     * @returns {Promise<object>}
     */
    async #lerResposta(resposta) {
      const corpo = await resposta.json();

      if (corpo.status) throw new Error(corpo.description || corpo.message);

      return corpo;
    }

    /**
     * Monta o corpo de `/cart/add.js`.
     *
     * Uma linha por combinação de variante e bordado — é isso que dá properties
     * por peça, mantém a baixa de estoque de cada produto e faz o pedido chegar
     * item a item no ERP. Peças iguais e sem bordado se juntam numa linha só,
     * com quantidade; personalizações diferentes nunca se juntam.
     *
     * O bordado é cobrado como produto de verdade, uma linha por peça bordada,
     * amarrada à peça por `_bordado_de`. É essa separação que resolve a dor de
     * expedição relatada na call de 07/09/2026: o bordado deixa de ser um valor
     * somado ao preço da peça e passa a ser item próprio na ordem de serviço.
     *
     * @returns {{comArte: {linha: object, arquivo: File}[], linhas: object[]}}
     */
    #montarPayload() {
      const grupos = new Map();

      // Cada envio ganha um lote próprio. Sem isso, montar um segundo kit
      // repetiria a posição 1, e as duas linhas de bordado — mesmo produto,
      // mesmas properties — se fundiriam numa só, sem como saber a qual peça
      // cada uma pertence.
      const lote = Date.now().toString(36);

      this.#selecao.forEach((unidade) => {
        const chave = `${unidade.varianteId}|${unidade.grupo ?? ''}|${JSON.stringify(unidade.bordado, assinarArquivo)}`;
        const grupo = grupos.get(chave);

        if (grupo) grupo.quantidade += 1;
        else grupos.set(chave, { unidade, quantidade: 1, posicao: `${lote}-${grupos.size + 1}` });
      });

      const comArte = [];
      const linhas = [];

      grupos.forEach(({ unidade, quantidade, posicao }) => {
        const [peca, ...cobrancas] = this.#linhasDoGrupo(unidade, quantidade, posicao);
        const arquivo = unidade.bordado?.logoArquivo;

        if (arquivo) comArte.push({ linha: peca, arquivo });
        else linhas.push(peca);

        // As cobranças nunca levam arquivo: a arte fica na peça, que é o item
        // que a produção separa. Elas cabem todas no lote.
        linhas.push(...cobrancas);
      });

      return { comArte, linhas };
    }

    /**
     * Linhas de carrinho de um grupo: a peça e, se houver, as cobranças do bordado.
     * @param {object} unidade
     * @param {number} quantidade
     * @param {string} posicao
     * @returns {object[]}
     */
    #linhasDoGrupo(unidade, quantidade, posicao) {
      const peca = {
        id: unidade.varianteId,
        quantity: quantidade,
        properties: { _kit: this.config.kit, ...this.#propriedadeDeGrupo(unidade) },
      };

      if (!unidade.bordado) return [peca];

      const referencia = `${this.config.kit}#${posicao}`;

      peca.properties = propriedadesDaPeca(unidade.bordado, {
        kit: this.config.kit,
        posicao,
        fonteNome: this.contrato.fonte?.nome ?? null,
      });
      Object.assign(peca.properties, this.#propriedadeDeGrupo(unidade));

      const cobrancas = linhasDeCobranca(unidade.bordado, {
        bordado: this.config.bordado,
        quantidade,
        referencia,
      });

      return [peca, ...cobrancas];
    }

    /**
     * `_kit_grupo` ("1" ou "2") da peça do combo, que a Function do desconto lê para
     * parear uma peça de cada grupo (ADR-0024). Nos outros modos não existe.
     *
     * Entra na linha do carrinho junto de `_kit`: peças iguais do mesmo grupo têm as
     * mesmas properties e continuam se fundindo numa linha com `quantity` somada.
     *
     * @param {object} unidade
     * @returns {{_kit_grupo?: string}}
     */
    #propriedadeDeGrupo(unidade) {
      return this.#ehCombo() && unidade.grupo ? { _kit_grupo: String(unidade.grupo) } : {};
    }

    /**
     * Abre o `<bordado-modal>` (ENN-90) para uma peça e resolve com o bordado no
     * formato de sempre da grade (`{campos, cor, fonte, logo, lado, logoArquivo}`),
     * ou com `null` no cancelamento — assim o chamador não lida com evento de dialog.
     *
     * A promessa resolve em `aoConfirmar` ou `aoFechar`, o que vier primeiro: o
     * `close` do dialog não é confiável para sinalizar a confirmação, e reabrir é o
     * caso normal aqui (um kit tem várias peças bordadas).
     *
     * @param {object} item
     * @param {{tamanhoFixo?: boolean}} [opcoes] `tamanhoFixo`: a unidade já entrou no kit
     *   com tamanho, então o modal não oferece trocá-lo. Não há `inicial`: a grade só
     *   pede bordado para peça sem bordado (não existe "editar", ver `#adicionarBordadoNaUnidade`).
     * @returns {Promise<object|null>}
     */
    #abrirModal(item, { tamanhoFixo = false } = {}) {
      const modal = this.modal;
      if (typeof modal?.abrir !== 'function') return Promise.resolve(null);

      const dados = item.bordadoPreview;
      const comFoto = Boolean(dados) && Boolean(FoxTheme.bordadoPreview) && Boolean(FoxTheme.bordadoGeometria);

      return new Promise((resolver) => {
        const abriu = modal.abrir({
          produto: null,
          foto: comFoto ? dados.foto : null,
          calibragem: comFoto ? dados.calibragem : null,
          tamanho: tamanhoFixo ? null : this.#fonteTamanho(item),
          icones: this.#icones(item),
          // Cada cor da loja é um produto (ADR-0016): a grade não troca de cor, só mostra a da peça.
          cor: item.cor?.nome ? { atual: item.cor, irmas: () => [item.cor], escolher: () => {} } : null,
          inicial: null,
          rodape: { rotulo: 'confirmar_bordado', exigeTamanho: !tamanhoFixo && Boolean(item.variantes), exigeBordado: true },
          aoConfirmar: ({ dados: escolhido, arquivoLogo, estado }) => {
            // Com `exigeBordado` o botão nem habilita sem bordado; é só defesa.
            if (!escolhido) return;

            const confirmado = {
              campos: escolhido.campos,
              cor: escolhido.cor,
              fonte: escolhido.fonte,
              logo: escolhido.logo,
              lado: escolhido.lado,
              logoArquivo: escolhido.logo ? arquivoLogo : null,
              icone: escolhido.icone ?? null,
              // Sem texto (só ícone e/ou logo): sem a linha `Bordado` (ADR-0027, D30).
              texto: Boolean(estado?.texto),
            };
            const impedimento = this.#impedimentoDoBordado(confirmado);
            if (impedimento) return impedimento;

            resolver(confirmado);
          },
          aoFechar: () => resolver(null),
        });

        // Sem o contrato do app o modal não abre: não deixa a promessa pendurada.
        if (abriu === false) resolver(null);
      });
    }

    /** Lista de ícones que o snippet `bordado-icones` entrega uma vez para a grade inteira. */
    #lerListaDeIcones() {
      try {
        const lista = JSON.parse(this.querySelector('[data-bordado-icones]')?.textContent ?? 'null');

        return lista?.lista?.length ? lista.lista : null;
      } catch {
        return null;
      }
    }

    /**
     * Lista fixa de ícones (`snippets/bordado-icones.liquid`, a mesma da PDP) com a
     * foto de posição da peça, ou `null` quando não há lista ou produto de cobrança —
     * aí o modal não oferece ícone e o resto não muda.
     * @param {object} item
     * @returns {{lista: object[], fotoPosicao: string, bordadoIcone: {calibragem: object, foto: string}|null}|null}
     */
    #icones(item) {
      if (!(this.config.bordado.iconeVarianteId > 0) || !this.listaDeIcones?.length) return null;

      return { lista: this.listaDeIcones, fotoPosicao: item.iconeFoto ?? '', bordadoIcone: item.bordadoIcone ?? null };
    }

    /**
     * Fonte de tamanho do modal: as variantes da peça no card, nos dois sentidos.
     * Escolher no modal grava em `#tamanhos` (e redesenha o card); o valor é o título
     * da variante. Nada muda a peça por fora enquanto o modal está aberto, então
     * `assinar` não tem o que avisar.
     * @param {object} item
     * @returns {{ler: Function, escolher: Function, assinar: Function}|null}
     */
    #fonteTamanho(item) {
      if (!item.variantes) return null;

      return {
        ler: () => ({
          opcoes: item.variantes.map((v) => ({ valor: v.titulo, disponivel: v.disponivel })),
          atual: this.#varianteEscolhida(item)?.titulo ?? null,
        }),
        escolher: (valor) => {
          const variante = item.variantes.find((v) => v.titulo === valor);
          if (variante) this.#escolherTamanho(item, variante.id);
        },
        assinar: () => () => {},
      };
    }

    /**
     * O que impede o bordado de ser confirmado, ou `null` se está de pé.
     *
     * A arte é obrigatória com o logo marcado. Sem ela a cobrança de logo entra
     * no pedido sem nada para a máquina bordar, e o que era uma venda vira
     * pendência de atendimento — que é justamente o tipo de custo operacional
     * que o ADR-0012 existe para tirar do caminho.
     *
     * @param {object} bordado
     * @returns {string|null}
     */
    #impedimentoDoBordado(bordado) {
      if (!bordado.logo) return null;
      if (!bordado.logoArquivo) return this.#t('logo_arquivo_faltando');
      if (bordado.logoArquivo.size > LIMITE_LOGO_MB * 1024 * 1024) {
        return this.#t('logo_arquivo_grande', { limite: LIMITE_LOGO_MB });
      }

      return null;
    }

    /**
     * Preenche a seleção da grade com as peças de um kit pronto — ENN-16, D19/D11. Único
     * ponto de entrada novo: o card de kit pronto (`assets/kit-prontos.js`) nunca calcula
     * preço nem manda ao carrinho por conta própria, só troca a seleção daqui e deixa o
     * resto do fluxo de sempre (bordado por peça, "Adicionar kit ao carrinho") continuar.
     *
     * Rolar a página até a grade e mover o foco é responsabilidade de quem chama, não
     * desta função.
     *
     * @param {object[]} pecas Mesmo shape de `itens[]`/`data-kit-prontos` (handle,
     *   titulo, varianteId, preco, disponivel, imagem).
     * @param {{nome?: string}} [opcoes] `nome` do kit, só para o anúncio ao leitor de
     *   tela.
     * @returns {boolean} `false`, sem tocar na seleção, se alguma peça não tiver
     *   `varianteId > 0` ou `disponivel === true` — a disponibilidade pode ter mudado
     *   entre a renderização do card e o clique.
     */
    preencher(pecas, { nome } = {}) {
      // Peça de várias variantes não tem tamanho definido no kit pronto: não preenche
      // (a grade a pede no cartão; o bloco da section nem habilita o botão).
      const invalida = (pecas || []).some(
        (peca) =>
          !(Number(peca.varianteId) > 0) ||
          peca.disponivel !== true ||
          peca.precisaTamanho === true ||
          Boolean(this.config.itens.find((i) => i.handle === peca.handle)?.variantes)
      );
      if (!Array.isArray(pecas) || pecas.length === 0 || invalida) return false;

      const podeBordar = Boolean(this.contrato) && this.config.bordado.varianteId > 0;

      this.#selecao = pecas.map((peca) => {
        // Preferimos o item da própria grade quando o handle existe nela: foto e
        // aceitaBordado já resolvidos ali são a fonte de verdade da página. Só quando a
        // peça não está na coleção da grade (Tweed e Star não estão — ver a spec) é que
        // resolvemos aceitaBordado com a mesma regra do connectedCallback.
        const item = this.config.itens.find((i) => i.handle === peca.handle);
        if (item) return { ...item, bordado: null };

        return { ...peca, aceitaBordado: podeBordar && this.contrato.handles.has(peca.handle), bordado: null };
      });

      this.#atualizar();
      this.#anunciarKitPronto(nome, pecas.length);
      return true;
    }

    /**
     * Anuncia ao leitor de tela que um kit pronto carregou a seleção, numa região
     * `aria-live="polite"` criada sob demanda (não existe uma pronta para isso nos dois
     * esqueletos — grade e lista — e criar uma nova aqui evita tocar nos dois templates
     * só por causa do kit pronto).
     * @param {string|undefined} nome
     * @param {number} quantidade
     */
    #anunciarKitPronto(nome, quantidade) {
      let regiao = this.querySelector('[data-papel="anuncio-kit-pronto"]');
      if (!regiao) {
        regiao = document.createElement('div');
        regiao.setAttribute('data-papel', 'anuncio-kit-pronto');
        regiao.setAttribute('aria-live', 'polite');
        regiao.setAttribute('role', 'status');
        regiao.className = 'visually-hidden';
        this.appendChild(regiao);
      }

      const chave = quantidade === 1 ? 'kit_pronto_carregado_one' : 'kit_pronto_carregado_other';
      regiao.textContent = this.#t(chave, { nome: nome || '', quantidade });
    }
  }

  // Expõe o cálculo de preço para o card de kit pronto (assets/kit-prontos.js) usar o
  // mesmo número que a grade mostra — ENN-16. `calcularKit` é fechada nesta IIFE, então
  // esta é a única forma de outro script chegar até ela.
  FoxTheme.kitBuilder = { calcularKit, propriedadesDaPeca, linhasDeCobranca };

  customElements.define('kit-builder', KitBuilder);
}
